import React from 'react'
import { useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'
import { Stack, Text, Header, Button, Loader } from '@crossfit/ui'

import { Affiliate } from 'types'
import Modal from 'components/Modal'
import { useNotification } from 'components/Notification'
import * as affiliatesService from 'services/affiliates'

type Props = {
  onClose: () => void
  affiliate: Affiliate
  token: string
}

function useDeclineInvite({ affiliate, token }: Omit<Props, 'onClose'>) {
  const form = useForm()
  const showNotification = useNotification()
  const navigate = useNavigate()

  async function handleSubmit() {
    try {
      await affiliatesService.respondToInvitation({
        token,
        affiliateId: affiliate?.id,
        accept: false,
      })

      showNotification(`You have declined the invitation`)
      navigate('/')
    } catch (error) {
      if (error.response?.status === 403) {
        showNotification(error.response.data.description, 'error')
        return
      }

      showNotification(error.message, 'error')
      throw error
    }
  }

  return {
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting: form.formState.isSubmitting,
  }
}

function DeclineInvite({ onClose, affiliate, token }: Props) {
  const { onSubmit, isSubmitting } = useDeclineInvite({ affiliate, token })

  return (
    <Modal
      onClose={onClose}
      header={
        <Header size='small' as='h3'>
          Decline Invite?
        </Header>
      }
    >
      {isSubmitting ? (
        <Loader />
      ) : (
        <form onSubmit={onSubmit}>
          <Text mb={8}>
            Are you sure you want to decline your invite to join the{' '}
            {affiliate?.name} team?
          </Text>
          <Stack
            direction='horizontal'
            style={{ justifyContent: 'flex-end' }}
            spacing={4}
          >
            <Button type='button' onClick={onClose} variant='secondary'>
              Cancel
            </Button>
            <Button type='submit' variant='destructive'>
              Decline Invite
            </Button>
          </Stack>
        </form>
      )}
    </Modal>
  )
}

export default DeclineInvite
